import React, { useEffect, useRef, useState } from "react";

import type { CreateMeetingRequestJson, ExtendedWorkspace } from "@performyard/service-contracts";

import { CalendarErrorModal } from "./CalendarErrorModal";
import MeetingForm, { type MeetingFormData } from "./MeetingForm";

import { useAddWorkspaceMeeting } from "../../../../../api/hooks";
import useForm from "../../../../../hooks/useForm";
import { useCalendarErrorHandler, useValidationErrorClearer } from "./hooks";

import { type MeetingFormSchema, meetingFormSchema } from "./meeting-form-schema";
import { type MeetingFormValuesWithOptionalDates, getDefaultFormValues } from "./meeting-form-utils";

type CreateMeetingModalProps = {
	isOpen: boolean;
	handleClose: () => void;
	workspace: ExtendedWorkspace;
	workspaceHasMeetings: boolean;
	setExpandedMeetingIds?: React.Dispatch<React.SetStateAction<string[]>>;
};

export const CreateMeetingModal = ({
	isOpen,
	handleClose,
	workspace,
	workspaceHasMeetings,
	setExpandedMeetingIds = () => {},
}: CreateMeetingModalProps): React.ReactElement => {
	const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof MeetingFormSchema, string>>>({});

	const { modalState: calendarModalState, handleCalendarError, closeModal, setRetrying } = useCalendarErrorHandler();

	const { mutate: addWorkspaceMeeting, isPending } = useAddWorkspaceMeeting(
		handleClose,
		setExpandedMeetingIds,
		(error, variables) => {
			const context = {
				operation: "create" as const,
				meetingData: variables.data,
				workspaceId: variables.workspaceId,
			};
			return handleCalendarError(error, context);
		}
	);

	const {
		validate,
		updateForm,
		state: formValues,
		setNewDefaultValues,
	} = useForm({
		schema: meetingFormSchema,
		defaultValues: getDefaultFormValues(),
		disableWarning: true,
	});

	const hasInitializedRef = useRef(false);

	useEffect(() => {
		if (isOpen && !hasInitializedRef.current) {
			setNewDefaultValues(getDefaultFormValues());
			setValidationErrors({});
			hasInitializedRef.current = true;
		} else if (!isOpen) {
			hasInitializedRef.current = false;
		}
	}, [isOpen, setNewDefaultValues]);

	// Cast to optional dates type since the form allows clearing dates during editing
	const formValuesWithOptionalDates = formValues as MeetingFormValuesWithOptionalDates;

	useValidationErrorClearer(formValuesWithOptionalDates, validationErrors, setValidationErrors);

	const handleSubmit = (data: MeetingFormData): void => {
		const validationResult = validate();
		if (!validationResult.isValid) {
			const errors = Object.entries(validationResult.errors || {}).reduce(
				(acc, [key, error]) => {
					acc[key as keyof MeetingFormSchema] = error.message;
					return acc;
				},
				{} as Partial<Record<keyof MeetingFormSchema, string>>
			);
			setValidationErrors(errors);
			return;
		}

		const meetingParticipants = workspace.participants?.map((participant) => ({
			userId: participant.userId,
			role: participant.role,
		}));

		const formattedData: CreateMeetingRequestJson = {
			...data,
			startDateTime: data.startDateTime.toISOString(),
			endDateTime: data.endDateTime.toISOString(),
			participants: meetingParticipants,
		};

		addWorkspaceMeeting({ workspaceId: workspace._id, data: formattedData });
	};

	const onClose = (): void => {
		if (calendarModalState.type !== "none") {
			closeModal();
			return;
		}

		handleClose();
		setValidationErrors({});
	};

	return (
		<>
			{calendarModalState.type === "none" && (
				<MeetingForm
					isOpen={isOpen}
					onClose={onClose}
					onSubmit={handleSubmit}
					formValues={formValuesWithOptionalDates}
					onFormChange={updateForm}
					validationErrors={validationErrors}
					submitLabel="Create Meeting"
					isPending={isPending}
					title="Create a New Meeting"
					showCarryOver={workspaceHasMeetings}
					showCalendarIntegration={true}
					workspaceId={workspace._id}
				/>
			)}

			<CalendarErrorModal
				modalState={calendarModalState}
				onRetry={() => {
					if (calendarModalState.type === "error") {
						setRetrying(true);
						const { context } = calendarModalState;
						if (context.operation === "create") {
							addWorkspaceMeeting(
								{ workspaceId: context.workspaceId, data: context.meetingData },
								{
									onSuccess: () => closeModal(),
									onError: () => setRetrying(false),
								}
							);
						}
					}
				}}
				onContinue={() => {
					if (calendarModalState.type === "error") {
						const { context } = calendarModalState;
						if (context.operation === "create") {
							const dataWithoutCalendar = { ...context.meetingData, addToCalendar: false };

							setRetrying(true);
							addWorkspaceMeeting(
								{ workspaceId: context.workspaceId, data: dataWithoutCalendar },
								{
									onSuccess: () => closeModal(),
									onError: () => setRetrying(false),
								}
							);
						}
					}
				}}
				onClose={closeModal}
			/>
		</>
	);
};
